export const userVisibleTabs = [
  {key: 'overview', path: 'overview', label: 'Overview'},
  {key: 'profile-details', path: '', label: 'Profile Details', end: true},
  {
    key: 'profile-history',
    path: 'profile-history',
    label: 'Profile Update History',
  },
  {
    key: 'account-activity',
    path: 'account-activity',
    label: 'Account Activity',
  },
  {
    key: 'payment-accounts',
    path: 'payment-accounts',
    label: 'Payment Accounts',
  },
  {key: 'kyc-status', path: 'kyc-status', label: 'KYC Status'},
  {key: 'portfolio', path: 'portfolio', label: 'User Portfolio'},
  {key: 'transactions', path: 'transactions', label: 'Transactions'},
  {key: 'wallet-settings', path: 'wallet-settings', label: 'Wallet Setting'},
  {key: 'interactions', path: 'interactions', label: 'User Interactions'},
  {key: 'support-ticket', path: 'support-ticket', label: 'Support tickets'},
  {key: 'claim-user', path: 'claim-user', label: 'Customer Success'},
  {
    key: 'persona',
    path: 'persona',
    label: 'User Persona',
    allowedRoles: [
      'SUPERADMIN',
      'ADMIN',
      'CUSTOMER_SUCCESS',
      'CUSTOMER_SUPPORT',
    ],
  },
  {key: 'risk-profile', path: 'risk-profile', label: 'Risk Profile'},
  {key: 'documents', path: 'documents', label: 'Documents'},
  {key: 'user-security', path: 'user-security', label: 'Security'},
  {key: 'referral', path: 'referral', label: 'Reward / Referral'},
  {key: 'investment', path: 'investment', label: 'Investment Details'},
  {key: 'liens', path: 'liens', label: 'Liens'},
  {key: 'interests', path: 'interests', label: 'Interest'},
  {key: 'subscription', path: 'subscription', label: 'Subscription'},
  {
    key: 'recurring-investment',
    path: 'recurring-investment',
    label: 'Recurring Investment',
  },
  {key: 'sessions', path: 'sessions', label: 'Recorded Sessions'},
  {
    key: 'notification-preferences',
    path: 'notification-preferences',
    label: 'Notification Preference',
  },
];

const CUSTOMER_SUCCESS_VISIBLE_TAB_KEYS = [
  'overview',
  'persona',
  'kyc-status',
  'account-activity',
  'support-ticket',
  'portfolio',
  'profile-details',
  'transactions',
  'wallet-settings',
  'claim-user',
  'interactions',
  'risk-profile',
  'documents',
  'referral',
  'recorded-sessions',
];

export const normalizeRoleNames = roleName =>
  String(roleName || '')
    .split(',')
    .map(role => role.trim().toUpperCase())
    .filter(Boolean);

export const hasAnyRole = (roleName, allowedRoles = []) => {
  const roles = normalizeRoleNames(roleName);
  const normalizedAllowedRoles = allowedRoles.map(role =>
    String(role || '')
      .trim()
      .toUpperCase(),
  );
  return normalizedAllowedRoles.some(role => roles.includes(role));
};

const filterByAllowedRoles = (tabs, roleName) =>
  tabs.filter(
    tab => !tab.allowedRoles || hasAnyRole(roleName, tab.allowedRoles),
  );

// Payment Accounts is opt-in. SUPERADMIN / ADMIN see it like every tab; any
// other role sees it only when it is assigned the `payment-accounts` USERS
// sub-tab, or holds the `PAYMENT_ACCOUNTS` permission the resolver checks.
// "All sub-tabs" and legacy roles don't pull it in, otherwise Customer Support
// and the rest would see a tab the gateway turns them away from.
const PAYMENT_ACCOUNTS_TAB_KEY = 'PAYMENT_ACCOUNTS';
export const PAYMENT_ACCOUNTS_KEY = 'payment-accounts';

const hasPaymentAccountsView = activeAdminTab => {
  const operations =
    activeAdminTab?.tabs?.find(tab => tab.tab === PAYMENT_ACCOUNTS_TAB_KEY)
      ?.operations || [];
  return operations.includes('VIEW') || operations.includes('ALL');
};

const defaultUserTabs = userVisibleTabs.filter(
  tab => tab.key !== PAYMENT_ACCOUNTS_KEY,
);

export const getVisibleUserTabs = roleName => {
  const allowedTabs = filterByAllowedRoles(defaultUserTabs, roleName);

  if (hasAnyRole(roleName, ['SUPERADMIN'])) return allowedTabs;
  if (hasAnyRole(roleName, ['CUSTOMER_SUCCESS'])) {
    return allowedTabs.filter(tab =>
      CUSTOMER_SUCCESS_VISIBLE_TAB_KEYS.includes(tab.key),
    );
  }

  return allowedTabs;
};

export const buildUserTabPath = (id, path) =>
  `/dashboard/user/${id}${path ? `/${path}` : ''}`;

export const canAccessTab = (roleName, tabKey) => {
  const visibleTabs = getVisibleUserTabs(roleName);
  return visibleTabs.some(tab => tab.key === tabKey);
};

// ─── Two-level RBAC: prefer the resolved USERS grant, fall back to the
//     legacy allowedRoles / CUSTOMER_SUCCESS behavior ───────────────────────
const USERS_TAB_KEY = 'USERS';

const isSuperAdminRole = activeAdminTab =>
  ['SUPERADMIN', 'ADMIN'].includes(activeAdminTab?.name);

const getUsersGrant = activeAdminTab =>
  activeAdminTab?.tabs?.find(tab => tab.tab === USERS_TAB_KEY) || null;

// Account Activity is the one sub-tab a role can reach without being granted
// it: the gateway accepts `USERS > account-activity` VIEW, `USERS > kyc-status`
// VIEW or `KYC_DASHBOARD` VIEW (a KYC agent then only gets Identity events).
// Mirror that any-of list, otherwise the tab is hidden from viewers the backend
// would let in.
const KYC_DASHBOARD_TAB_KEY = 'KYC_DASHBOARD';
const ACCOUNT_ACTIVITY_KEY = 'account-activity';
const KYC_STATUS_KEY = 'kyc-status';

const hasKycDashboardView = activeAdminTab => {
  const operations =
    activeAdminTab?.tabs?.find(tab => tab.tab === KYC_DASHBOARD_TAB_KEY)
      ?.operations || [];
  return operations.includes('VIEW') || operations.includes('ALL');
};

// The user-detail inner tabs map to the USERS tab's sub-tabs. Their `key`s line
// up 1:1 with the catalog sub-tab keys, so no remapping is needed.
const getUsersSubTabsForGrant = activeAdminTab => {
  // SUPERADMIN / ADMIN bypass — see everything.
  if (isSuperAdminRole(activeAdminTab)) return userVisibleTabs;

  const grant = getUsersGrant(activeAdminTab);

  // New-style grant: all sub-tabs allowed.
  if (grant?.allSubTabs === true) return defaultUserTabs;

  // New-style grant: explicit sub-tabs → only those keys are visible.
  if (grant?.subTabs?.length) {
    const grantedKeys = grant.subTabs.map(sub => sub.subTab);
    const keys =
      grantedKeys.includes(KYC_STATUS_KEY) ||
      hasKycDashboardView(activeAdminTab)
        ? [...grantedKeys, ACCOUNT_ACTIVITY_KEY]
        : grantedKeys;
    return userVisibleTabs.filter(tab => keys.includes(tab.key));
  }

  // Legacy role (no USERS sub-tab grant) → existing role-array behavior.
  return getVisibleUserTabs(activeAdminTab?.name);
};

export const getVisibleUserTabsForGrant = activeAdminTab => {
  const tabs = getUsersSubTabsForGrant(activeAdminTab);
  if (!hasPaymentAccountsView(activeAdminTab)) return tabs;
  if (tabs.some(tab => tab.key === PAYMENT_ACCOUNTS_KEY)) return tabs;
  // Keep the catalog order so the tab sits next to Account Activity.
  return userVisibleTabs.filter(
    tab => tab.key === PAYMENT_ACCOUNTS_KEY || tabs.includes(tab),
  );
};

export const canAccessTabForGrant = (activeAdminTab, tabKey) => {
  const visibleTabs = getVisibleUserTabsForGrant(activeAdminTab);
  return visibleTabs.some(tab => tab.key === tabKey);
};
